import React, { useState, useEffect } from "react";
import { Activity, Users, Building2, TrendingUp, Calendar, Eye, ExternalLink } from "lucide-react";
import { useHistory } from "react-router-dom";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend,
} from "recharts";

const API_URL = process.env.REACT_APP_API_URL || "https://api.littleexplorers.help";

const OverallAnalyticsCharts = ({ getAccessToken }) => {
  const history = useHistory();
  const [analytics, setAnalytics] = useState(null);
  const [timeSeriesData, setTimeSeriesData] = useState([]);
  const [topOrgsData, setTopOrgsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(30);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const token = getAccessToken();
        
        // Fetch platform-wide overview
        const response = await fetch(
          `${API_URL}/v2/analytics/overview?days=${days}`,
          {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch analytics: ${response.status}`);
        }

        const data = await response.json();
        setAnalytics(data);

        // Format trend data for chart
        const formattedTrend = (data.trend || []).map(item => ({
          date: new Date(item.date).toLocaleDateString("en-US", { 
            month: "short", 
            day: "numeric" 
          }),
          fullDate: item.date,
          views: item.views || 0,
        }));
        setTimeSeriesData(formattedTrend);

        // Prepare top organizations data with names
        if (data.topOrganizations) {
          const formattedOrgs = data.topOrganizations
            .slice(0, 10)
            .map(org => {
              const displayName = org.org_name || org.org_id;
              return {
                id: org.org_id,
                name: displayName.length > 25 
                  ? `${displayName.substring(0, 25)}...`
                  : displayName,
                fullName: displayName,
                views: org.views || 0,
                uniqueViewers: org.unique_viewers || 0,
              };
            });
          setTopOrgsData(formattedOrgs);
        }
      } catch (err) {
        console.error("Error fetching overall analytics:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, [days, getAccessToken]);

  const handleOrgClick = (orgId) => {
    history.push(`/org-management?orgId=${orgId}`);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-gray-900 border border-gray-700 rounded-lg p-3 shadow-xl">
          <p className="text-gray-300 text-sm mb-2">{label}</p>
          {payload.map((entry) => (
            <p key={`${entry.name}-${entry.value}`} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {entry.value.toLocaleString()}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const OrgTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = topOrgsData.find(o => o.name === label);
      return (
        <div className="bg-gray-900 border border-gray-700 rounded-lg p-3 shadow-xl max-w-xs">
          <p className="text-white font-medium mb-2">{data?.fullName || label}</p>
          <p className="text-gray-400 text-xs mb-2">Click to view organization</p>
          {payload.map((entry) => (
            <p key={`${entry.name}-${entry.value}`} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {entry.value.toLocaleString()}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="p-4 bg-gray-800 rounded-lg flex items-center gap-2 text-gray-400">
        <Activity className="w-4 h-4 animate-spin" />
        Loading platform analytics...
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-900/20 border border-red-500/30 rounded-lg text-red-400">
        Error loading analytics: {error}
      </div>
    );
  }

  if (!analytics || !analytics.overview) {
    return (
      <div className="p-4 bg-gray-800/50 rounded-lg text-gray-400">
        No analytics data available.
      </div>
    );
  }

  const { overview } = analytics;

  // Calculate average
  const avgViewsPerDay = timeSeriesData.length > 0 
    ? (timeSeriesData.reduce((sum, day) => sum + day.views, 0) / timeSeriesData.length).toFixed(0)
    : 0;

  return (
    <div className="space-y-6">
      {/* Header with Period Selector */}
      <div className="flex items-center justify-between">
        <h3 className="text-white font-medium flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-indigo-400" />
          Platform Analytics Overview
        </h3>
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="bg-gray-700 border border-gray-600 text-white text-sm rounded px-3 py-1"
        >
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-gray-800/50 p-3 rounded-lg border border-gray-700">
          <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
            <Eye className="w-4 h-4" />
            Total Views
          </div>
          <div className="text-2xl font-bold text-white">
            {overview.total_views?.toLocaleString() || 0}
          </div>
        </div>

        <div className="bg-gray-800/50 p-3 rounded-lg border border-gray-700">
          <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
            <Users className="w-4 h-4" />
            Unique Viewers
          </div>
          <div className="text-2xl font-bold text-white">
            {overview.unique_viewers?.toLocaleString() || 0}
          </div>
        </div>

        <div className="bg-gray-800/50 p-3 rounded-lg border border-gray-700">
          <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
            <Building2 className="w-4 h-4" />
            Orgs Viewed
          </div>
          <div className="text-2xl font-bold text-white">
            {overview.orgs_viewed?.toLocaleString() || 0}
          </div>
        </div>

        <div className="bg-gray-800/50 p-3 rounded-lg border border-gray-700">
          <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
            <Calendar className="w-4 h-4" />
            Avg Views/Day
          </div>
          <div className="text-2xl font-bold text-green-400">
            {avgViewsPerDay}
          </div>
        </div>
      </div>

      {/* Views Over Time Chart */}
      {timeSeriesData.length > 0 && (
        <div className="bg-gray-800/50 rounded-lg border border-gray-700 p-4">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-white font-medium flex items-center gap-2">
              <Calendar className="w-4 h-4 text-indigo-400" />
              Platform Views Over Time
            </h4>
            <div className="text-sm text-gray-400">
              {timeSeriesData.length} days tracked
            </div>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={timeSeriesData}>
                <defs>
                  <linearGradient id="colorPlatformViews" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#818cf8" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#818cf8" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis 
                  dataKey="date" 
                  stroke="#9ca3af"
                  fontSize={12}
                  tickLine={false}
                />
                <YAxis 
                  stroke="#9ca3af"
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="views"
                  name="Total Views"
                  stroke="#818cf8"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorPlatformViews)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Top Organizations Section */}
      {topOrgsData.length > 0 && (
        <div className="bg-gray-800/50 rounded-lg border border-gray-700 p-4">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-white font-medium">Top Organizations by Views</h4>
            <span className="text-sm text-gray-400">Click any organization to view details</span>
          </div>
          
          {/* Chart */}
          <div className="h-80 mb-6">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={topOrgsData} layout="vertical" margin={{ left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" horizontal={false} />
                <XAxis 
                  type="number" 
                  stroke="#9ca3af"
                  fontSize={12}
                />
                <YAxis 
                  type="category" 
                  dataKey="name" 
                  stroke="#9ca3af"
                  fontSize={10}
                  width={150}
                  tickLine={false}
                />
                <Tooltip content={<OrgTooltip />} />
                <Legend />
                <Bar 
                  dataKey="views" 
                  name="Total Views" 
                  fill="#818cf8"
                  radius={[0, 4, 4, 0]}
                />
                <Bar 
                  dataKey="uniqueViewers" 
                  name="Unique Viewers" 
                  fill="#34d399"
                  radius={[0, 4, 4, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Clickable List */}
          <div className="border-t border-gray-700 pt-4">
            <h5 className="text-gray-300 text-sm mb-3">Quick Navigation</h5>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {topOrgsData.map((org) => (
                <button
                  type="button"
                  key={org.id}
                  onClick={() => handleOrgClick(org.id)}
                  className="flex items-center justify-between p-3 bg-gray-700/50 hover:bg-gray-700 rounded-lg transition-colors text-left group"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium truncate">{org.fullName}</p>
                    <p className="text-gray-400 text-xs">{org.views.toLocaleString()} views</p>
                  </div>
                  <ExternalLink className="w-4 h-4 text-gray-500 group-hover:text-indigo-400 flex-shrink-0 ml-2" />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OverallAnalyticsCharts;
